import React, { HTMLAttributeAnchorTarget } from 'react'
import { useRouter } from 'next/router'
import Link from 'components/Link'
import CarouselPropertyImagesSmall from 'components/carousels/CarouselPropertyImagesSmall'
import { joinBy } from 'data/helpers/strings'
import { propertyPrice } from 'data/helpers/properties'
import { Property } from 'gql/graphql'
import classNames from 'classnames'

type PropTypes = {
  property: Property
  target?: HTMLAttributeAnchorTarget
  isGraphQL?: boolean
  isMapCard?: boolean
  variant?: 'show-wishlist' | 'hide-wishlist'
}

const PropertyCard = ({
  property,
  isGraphQL = true,
  target = '_self',
  variant = 'show-wishlist',
  isMapCard = false,
}: PropTypes) => {
  const router = useRouter()

  let url = `/${property.url}`
  if (router.query) {
    url += '?'
    url += router.query.arrival && router.query.departure ? `arrival=${router.query.arrival}&departure=${router.query.departure}` : ''
    url += router.query.bedrooms ? `&bedrooms=${router.query.bedrooms}` : ''
    url += router.query.adults ? `&adults=${router.query.adults}` : ''
    url += router.query.children ? `&children=${router.query.children}` : ''
  }

  return (
    <Link className="cursor-pointer" href={url} target={target} key={`property-${property.id}`}>
      <div className="flex flex-col">
        <div className="relative aspect-h-5 aspect-w-7">
          <CarouselPropertyImagesSmall
            propertyMainImage={{
              media: isGraphQL
                ? property.main_image?.url
                : property.main_image,
              alt_text: property.main_image_alt_text ?? property.main_image?.file_name ?? property?.title,
            }}
            propertyImages={property.images.map(image => ({
              media: image.url,
              alt_text: image.file_name,
            }))}
            propertyId={variant === 'hide-wishlist' ? undefined : property.id}
            imageWidth="706"
          />
        </div>
        <div className={classNames('flex items-start justify-between leading-20', {
          'text-12 sm:text-14 mt-5 sm:mt-10': isMapCard,
          'text-14 mt-10': !isMapCard,
        })}>
          <p className="text-primary">
            {joinBy([property.city?.value, property.region?.value])}
          </p>
          <p className="text-grey-500">
                        Ref. {property.reference}
          </p>
        </div>
        <h3 className={classNames('font-serif tracking-tight leading-title text-grey-900', {
          'text-22 sm:text-26 mt-5 sm:mt-15': isMapCard,
          'text-26 mt-15': !isMapCard,
        })}>
          {property.title}
        </h3>
        <p className={classNames('leading-20 font-normal text-grey-800', {
          'text-12 sm:text-14 mt-3 sm:mt-[7px]': isMapCard,
          'text-14 mt-[7px]': !isMapCard,
        })}>
          {joinBy([
            property.guests ? `${property.guests} Guests` : null,
            property.bedrooms ? `${property.bedrooms} Bedrooms` : null,
            property.bathrooms ? `${property.bathrooms} Bathrooms` : null,
          ], ' - ')}
        </p>
        <p className={classNames('leading-20 text-grey-1100 opacity-40', {
          'text-12 sm:text-14 mt-3 sm:mt-[7px]': isMapCard,
          'text-14 mt-[7px]': !isMapCard,
        })}>
          {propertyPrice(property)}
        </p>
      </div>
    </Link>
  )
}

export default PropertyCard
